import { z } from 'zod';

/**
 * Validation schemas for ADDAPT forms using Zod
 */

// Profile creation schema based on API requirements
export const profileCreationSchema = z.object({
  profileName: z
    .string()
    .min(1, 'Profile name is required')
    .max(255, 'Profile name must be 255 characters or less'),
  
  ldap: z
    .string()
    .min(1, 'LDAP group is required')
    .max(255, 'LDAP group must be 255 characters or less'),
  
  description: z
    .string()
    .min(1, 'Description is required')
    .max(1000, 'Description must be 1000 characters or less')
});

// Type inference from schema
export type ProfileCreationInput = z.infer<typeof profileCreationSchema>;

// Job creation schema
export const jobCreationSchema = z.object({
  jobName: z
    .string()
    .min(1, 'Job name is required')
    .max(255, 'Job name must be 255 characters or less'),
  
  jobId: z
    .string()
    .optional(),
  
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less'),
  
  jobType: z
    .enum(['DRAFT', 'RELEASE'])
    .optional()
});

export type JobCreationInput = z.infer<typeof jobCreationSchema>;

// Job edit/update schema
export const jobUpdateSchema = z.object({
  name: z
    .string()
    .min(1, 'Job name is required')
    .max(255, 'Job name must be 255 characters or less')
    .trim(),
  
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less')
    .optional()
    .or(z.literal('')),
  
  type: z
    .enum(['ondemand', 'scheduled'])
    .optional(),
  
  scheduleTime: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(val);
    }, 'Invalid time format (HH:mm)'),
  
  scheduleDate: z
    .string()
    .optional()
});

export type JobUpdateInput = z.infer<typeof jobUpdateSchema>;

// Template generation schema
export const templateGenerationSchema = z.object({
  userId: z.string().min(1, 'User ID is required'),
  
  profile: z.string().optional(),
  
  questionAnswers: z.record(z.string()).optional(),
  
  excelSheetUri: z
    .string()
    .regex(/^s3:\/\//, 'Must be a valid S3 URI')
    .optional(),
  
  existingTemplateId: z.string().optional(),
  
  feedback: z.string().max(5000, 'Feedback must be 5000 characters or less').optional(),
  
  jobId: z.string().optional()
}).refine(
  (data) => data.questionAnswers || data.excelSheetUri,
  {
    message: 'Either question answers or Excel file URI must be provided',
  }
);

export type TemplateGenerationInput = z.infer<typeof templateGenerationSchema>;

// Helper function to format Zod errors for display
export function formatZodErrors(error: z.ZodError): Record<string, string> {
  const formattedErrors: Record<string, string> = {};
  
  error.errors.forEach((err) => {
    const path = err.path.join('.');
    formattedErrors[path] = err.message;
  });
  
  return formattedErrors;
}

// Validation helper with better error messages
export function validateWithSchema<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  try {
    const validData = schema.parse(data);
    return { success: true, data: validData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { success: false, errors: formatZodErrors(error) };
    }
    throw error;
  }
}
